// Live mode on the device page (docs/device-protocol.md §16): the badge with the time left, each probe's value
// with a short pulse when it changes, how long ago it was taken, and a small curve of the last 10 minutes.

import { useI18n } from '../i18n/index.ts';
import { type DeviceLive, LIVE_WINDOW_MS, type ProbeLive } from '../lib/deviceLive.ts';
import { formatAgo, formatDuration, formatTemp } from '../lib/format.ts';
import { Badge, Button } from './ui.tsx';

export function LiveBadge({ live }: { live: DeviceLive }) {
  const { t, lang } = useI18n();
  const { status, session, now } = live;
  if (status === 'unavailable') return null;
  if (status === 'paused') {
    return (
      <span className="flex items-center gap-2">
        <Badge tone="slate">{t('devices.live.paused')}</Badge>
        <Button size="sm" variant="ghost" onClick={live.resume}>{t('devices.live.resume')}</Button>
      </span>
    );
  }
  if (status === 'live' && session) {
    const left = Math.max(0, Math.round((Date.parse(session.live_until) - now) / 1000));
    return (
      <span className="flex items-center gap-2" role="status">
        <Badge tone="green">
          <span className="relative flex size-2" aria-hidden="true">
            <span className="absolute inline-flex size-full animate-ping rounded-full bg-emerald-500 opacity-60 motion-reduce:hidden" />
            <span className="relative inline-flex size-2 rounded-full bg-emerald-500" />
          </span>
          {t('devices.live.on')}
        </Badge>
        <span className="text-xs text-slate-500">
          {t('devices.live.every', { s: live.intervalS ?? session.interval_s })} · {t('devices.live.left',
            { time: formatDuration(left, lang) })}
        </span>
      </span>
    );
  }
  const by = session?.expected_by ? Math.max(0, Math.round((Date.parse(session.expected_by) - now) / 1000)) : null;
  return (
    <span className="flex items-center gap-2" role="status">
      <Badge tone="amber">{t('devices.live.starting')}</Badge>
      <span className="text-xs text-slate-500">
        {by !== null && by > 0 ? t('devices.live.waitingUntil', { s: by }) : t('devices.live.waiting')}
      </span>
    </span>
  );
}

/** The live value of one probe: large, pulsing briefly when it changes, with its age and the curve. */
export function LiveValue({ probe, now, fallback }: { probe: ProbeLive | undefined; now: number;
  fallback: { value: number | null; at: string | null } }) {
  const { t, lang } = useI18n();
  const last = probe?.last;
  const value = last ? last.value : fallback.value;
  const at = last ? new Date(last.at).toISOString() : fallback.at;
  const fresh = probe && probe.changedAt > 0 && now - probe.changedAt < 1500;
  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
      <div>
        <span key={probe?.changedAt ?? 0} className={`block whitespace-nowrap rounded px-1 text-2xl font-semibold tabular-nums
          text-slate-900 ${fresh ? 'live-flash' : ''}`}>
          {formatTemp(value, lang)}
        </span>
        <span className="block whitespace-nowrap px-1 text-xs text-slate-500">{at ? formatAgo(at, lang, now) : t('devices.live.noValue')}</span>
      </div>
      {probe && probe.points.length > 1 && <Sparkline points={probe.points} now={now}
        title={(min, max) => t('devices.live.curve', { min: formatTemp(min, lang), max: formatTemp(max, lang) })} />}
    </div>
  );
}

/** A light curve of the last 10 minutes (SVG): the line, with the lowest and highest values as its title. */
export function Sparkline({ points, now, title: describe, width = 120, height = 36 }: { points: [number, number][];
  now: number; title: (min: number, max: number) => string; width?: number; height?: number }) {
  const values = points.map((p) => p[1]);
  const min = Math.min(...values);
  const max = Math.max(...values);
  const pad = Math.max(0.1, (max - min) * 0.15);
  const lo = min - pad;
  const hi = max + pad;
  const x = (tMs: number) => ((tMs - (now - LIVE_WINDOW_MS)) / LIVE_WINDOW_MS) * width;
  const y = (v: number) => height - ((v - lo) / (hi - lo)) * height;
  const d = points.map(([tMs, v], i) => `${i === 0 ? 'M' : 'L'}${x(tMs).toFixed(1)},${y(v).toFixed(1)}`).join(' ');
  const [lastT, lastV] = points[points.length - 1]!;
  const title = describe(min, max);
  return (
    <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} role="img" aria-label={title}
      className="shrink-0 overflow-visible text-brand-500">
      <title>{title}</title>
      <line x1={0} x2={width} y1={height} y2={height} className="stroke-slate-200" strokeWidth={1} />
      <path d={d} fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinejoin="round" strokeLinecap="round" />
      <circle cx={x(lastT)} cy={y(lastV)} r={2.5} fill="currentColor" />
    </svg>
  );
}
